import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAbsoluteNavigate } from './useAbsoluteNavigate';
import { logException } from '@/utils/logger';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { logout } from '@/store/actions/auth';
import { useLogin } from '@/hooks/useLogin';
import { closeDrawer } from '@/store/ui/actions';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { useModal } from '@/features/modal/useModal';
import { BffAuthSessionCheckResponse } from '@lucky7ventures/bff-types';

// This session check was used on the root app level before. Now as we are fully
// migrated to the BFF with proper response codes (e.g. unauthorized), we don't
// need it executing everywhere anymore - only on the :game page, because
// this hook was introduced in the first place due to some session problems
// for the user while playing games.
// We also amended it from firing every 15 seconds to 60 seconds.
export function useCheckSession(): void {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const absoluteNavigate = useAbsoluteNavigate();
  const dispatch = useDispatch();
  const { openLogin } = useLogin();
  const { request } = useBffApiRequest<BffAuthSessionCheckResponse>();
  const { closeModal } = useModal();

  useEffect(() => {
    let interval: NodeJS.Timer;

    if (isAuthenticated) {
      interval = setInterval(() => {
        request({
          apiMethod: BffApiService.checkSession,
          successCallback: data => {
            if (data === false) {
              triggerGTag(GTagEvents.ping_login_failed);
              dispatch(logout());
              absoluteNavigate('/');
              // close modals and drawers if any is open
              dispatch(closeModal());
              dispatch(closeDrawer());
              openLogin({ openedFrom: OpenedFrom.PingLogin, isLoggedOut: true });
            }
          },
          errorCallback: error => {
            logException(error);
          },
        });
      }, 60000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isAuthenticated]);
}
